import { useEffect, useRef } from 'react';
import {
  useFocusable as useNoriginFocusable,
  FocusableComponentLayout,
  UseFocusableConfig,
} from '@noriginmedia/norigin-spatial-navigation';
import { useNavigationStore } from '@/stores/navigationStore';
import { useAppStore } from '@/stores/appStore';

export interface UseFocusableOptions extends UseFocusableConfig {
  focusKey?: string;
  onEnterPress?: () => void;
  onArrowPress?: (direction: string) => boolean;
  onFocus?: (focusComponentLayout: FocusableComponentLayout) => void;
  onBlur?: () => void;
  onMouseEnter?: () => void;
  onBackPress?: () => boolean | void; // 返回键回调，返回 true 表示已处理（阻止传播），false 或 void 表示未处理（继续传播）
  onDelete?: () => boolean | void; // 删除键回调，返回 true 表示已处理（阻止传播），false 或 void 表示未处理（继续传播）
  trackChildren?: boolean;
  isFocusBoundary?: boolean;
  preferredChildFocusKey?: string; // 优先子焦点key
  [key: string]: any; // 允许传递其他属性到底层库
}

export enum Direction {
  UP = 'up',
  DOWN = 'down',
  LEFT = 'left',
  RIGHT = 'right',
}

// 用于跟踪已被页面级处理的返回键事件
const handledBackKeyEvents = new WeakSet<KeyboardEvent>();

// 用于跟踪已被页面级处理的删除键事件
const handledDeleteKeyEvents = new WeakSet<KeyboardEvent>();

// 检查是否为返回键
const isBackKey = (keyCode: number): boolean => {
  const win = window as any;
  return (
    keyCode === 10009 || // 三星返回键
    keyCode === 461 || // LG返回键
    keyCode === win.VK_BACK
  );
};

// 检查是否为删除键
const isDeleteKey = (keyCode: number): boolean => {
  return (
    keyCode === 8 || // Backspace
    keyCode === 27 // ESC
  );
};

// 导出函数供全局处理器检查事件是否已被处理
export const isBackKeyHandled = (event: KeyboardEvent): boolean => {
  return handledBackKeyEvents.has(event);
};

export const useFocusable = (options: UseFocusableOptions) => {
  const {
    focusKey,
    onEnterPress,
    onArrowPress,
    onFocus,
    onBlur,
    onBackPress,
    onDelete,
    preferredChildFocusKey,
    ...rest
  } = options;
  const setCurrentFocusKey = useNavigationStore((state) => state.setCurrentFocusKey);
  const addToHistory = useNavigationStore((state) => state.addToHistory);
  const emit = useAppStore((state) => state.emit);
  const onBackPressRef = useRef(onBackPress);
  const onDeletePressRef = useRef(onDelete);
  const focusKeyRef = useRef(focusKey);
  const hasLoggedRef = useRef(false);

  // 保持 onBackPress 引用最新
  useEffect(() => {
    onBackPressRef.current = onBackPress;
  }, [onBackPress]);

  // 保持 onDelete 引用最新
  useEffect(() => {
    onDeletePressRef.current = onDelete;
  }, [onDelete]);

  // 保持 focusKey 引用最新
  useEffect(() => {
    focusKeyRef.current = focusKey;
  }, [focusKey]);

  // 只在第一次注册时打印日志（每个组件实例只打印一次）
  useEffect(() => {
    if (!hasLoggedRef.current) {
      hasLoggedRef.current = true;
      if (process.env.NODE_ENV === 'development') {
        console.log(`[useFocusable] 注册焦点元素: ${focusKey}`);
      }
    }
  }, [focusKey]);

  const { ref, focused, focusSelf } = useNoriginFocusable({
    focusKey,
    preferredChildFocusKey,
    onEnterPress: () => {
      console.log(`[useFocusable] ${focusKey} Enter键按下`);
      emit('focus:enter', { focusKey });
      onEnterPress?.();
    },
    onArrowPress: (direction) => {
      console.log(`[useFocusable] ${focusKey} 方向键按下:`, direction);
      emit('focus:arrow', { focusKey, direction });
      // 返回值：false = 阻止导航，true = 允许导航
      // 默认返回 true（允许导航）
      return onArrowPress ? onArrowPress(direction) : true;
    },
    onFocus: (focusComponentLayout: FocusableComponentLayout) => {
      console.log(`[useFocusable] ${focusKey} 获得焦点`);
      setCurrentFocusKey(focusKey || '');
      addToHistory(focusKey || '');
      emit('focus:change', { focusKey });
      onFocus?.(focusComponentLayout);
    },
    onBlur: () => {
      console.log(`[useFocusable] ${focusKey} 失去焦点`);
      onBlur?.();
    },
    ...rest, // rest 中不包含 onBackPress 和 preferredChildFocusKey，因为已经在解构时分离了
  });

  // 监听返回键和删除键事件
  useEffect(() => {
    if (!onBackPressRef.current && !onDeletePressRef.current) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      // 使用 ref 获取最新的 focusKey
      const latestFocusKey = focusKeyRef.current;
      // 从 store 获取当前焦点 key（不通过订阅）
      const latestCurrentFocusKey = useNavigationStore.getState().currentFocusKey;

      // 只有当当前焦点在当前元素上时才处理返回键
      if (latestCurrentFocusKey === latestFocusKey && isBackKey(event.keyCode)) {
        console.log(`[useFocusable] ${latestFocusKey} 返回键被按下`);
        event.preventDefault();

        // 调用回调并检查返回值
        const handled = onBackPressRef.current?.();

        // 如果回调返回 true，阻止事件传播（表示已处理）
        // 如果返回 false 或 undefined，允许事件继续传播到全局处理器
        if (handled === true) {
          event.stopPropagation();
          // 将事件标记为已处理，防止全局处理器重复处理
          handledBackKeyEvents.add(event);
          console.log(`[useFocusable] ${latestFocusKey} 返回键已被处理，阻止传播`);
        } else {
          console.log(`[useFocusable] ${latestFocusKey} 返回键未被处理，允许传播到全局处理器`);
        }
      }

      // 处理删除键
      if (latestCurrentFocusKey === latestFocusKey && isDeleteKey(event.keyCode)) {
        const deleteHandler = onDeletePressRef.current || onBackPressRef.current;
        if (deleteHandler) {
          console.log(`[useFocusable] ${latestFocusKey} 删除键被按下`);
          event.preventDefault();

          const handled = deleteHandler();

          if (handled === true) {
            event.stopPropagation();
            // 将事件标记为已处理，防止全局处理器重复处理
            handledDeleteKeyEvents.add(event);
            console.log(`[useFocusable] ${latestFocusKey} 删除键已被处理，阻止传播`);
          } else {
            console.log(`[useFocusable] ${latestFocusKey} 删除键未被处理，允许传播到全局处理器`);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true); // 使用 capture 阶段
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []); // 空依赖数组，只在组件挂载时注册一次

  return { ref, focused, focusSelf, focusKey };
};
